import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { NotificationDto } from '@/api/Clients/Types/NotificationDto'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { useAppDispatch, useAppSelector } from '@/store'
import { fetchClientAreaDomain } from '@/store/clientarea/clientarea.actions'
import { ClientAreaApiClient } from '@/utils/api/clientAreaApi'
import { isUUID } from '@/utils/string.utils'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links, notificationDetailsRouteUrl } from 'utils/featureToggles'

import NotificationDetailsPage from './NotificationDetailsPage'

export enum NotificationTab {
  Details = 'details',
}

const crumbsState = [{ title: 'Notifications', link: links.marketingNotifications.url }]

const useResolveNotificationRouteFetch = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const clonedData = location.state?.clonedNotification as NotificationDto

  const [crumbs, setCrumbs] = useState(crumbsState)
  const walletsApiClient = useApiClient(ClientAreaApiClient)
  const domain = useAppSelector((state) => state.clientArea.domain)
  const dispatch = useAppDispatch()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    if (id && isUUID(id) && domain?.clientAreaDomain) {
      return walletsApiClient.getNotificationById(id, domain.clientAreaDomain)
    }
    return Promise.resolve(null)
  }, [id, domain?.clientAreaDomain])
  const { data: notification, isLoading } = useFetchOne(callback)

  const resolvedNotification = id === 'add-new' && clonedData ? clonedData : notification

  useEffect(() => {
    if (!domain) {
      dispatch(fetchClientAreaDomain(walletsApiClient))
    }

    if (resolvedNotification && id !== 'add-new') {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: resolvedNotification.name,
          link: notificationDetailsRouteUrl(resolvedNotification, NotificationTab.Details),
        },
      ]
      setCrumbs([crumb1, crumb2])
    } else if (id === 'add-new') {
      setCrumbs(crumbsState)
    }
  }, [resolvedNotification, id]) // eslint-disable-line react-hooks/exhaustive-deps

  return { crumbs, isLoading, notification: resolvedNotification, forceRefresh }
}

const ResolveNotificationRoute = () => {
  const { id } = useParams<{ id: string }>()

  const { crumbs, isLoading, notification, forceRefresh } = useResolveNotificationRouteFetch()

  const innerRender = () => {
    if (isLoading && id !== 'add-new') {
      return <Spinner />
    }

    if (!notification && id !== 'add-new') {
      return <PageNotFound />
    }

    return (
      <React.Fragment>
        <Breadcrumbs crumbs={crumbs} />
        <NotificationDetailsPage data={notification ?? undefined} forceRefresh={forceRefresh} />
      </React.Fragment>
    )
  }

  return <Loading isLoading={isLoading && id !== 'add-new'}>{innerRender()}</Loading>
}

export default ResolveNotificationRoute
